import { FC, useMemo } from 'react';
import { useSortBy, useTable } from 'react-table';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiTrafficBySource, {
  TrafficBySourceRow,
} from 'hooks/useRoiTrafficBySource';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';
import {
  TRAFFIC_BY_SOURCE_START_DATE,
  TRAFFIC_SOURCES,
} from './TRAFFIC_SOURCES';
import moment from 'moment';

interface TrafficBySourceProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

interface TrafficBySourceTableRow extends TrafficBySourceRow {
  label: string;
}

const SHOWS_PLACEHOLDER = '—';
const COLUMN_COUNT = 9;

const formatCount = (value: number) => (value ?? 0).toLocaleString();

const emptyRow = (key: string, label: string): TrafficBySourceTableRow => ({
  source: key,
  label,
  openedWidget: 0,
  onlineAppts: 0,
  totalAppts: 0,
  shows: 0,
  totalNewPatients: 0,
  totalExistingPatients: 0,
  totalProduction: 0,
  productionPerAppt: 0,
});

const TrafficBySource: FC<TrafficBySourceProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data, isLoading, isValidating } = useRoiTrafficBySource({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const totals = data?.periodData?.totals;

  const rows = useMemo<TrafficBySourceTableRow[]>(() => {
    const bySource = new Map(
      (data?.periodData?.trafficBySource ?? []).map((row) => [row.source, row])
    );

    return TRAFFIC_SOURCES.map(({ key, label }) => {
      const apiRow = bySource.get(key);
      return apiRow ? { ...apiRow, label } : emptyRow(key, label);
    });
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: 'Source',
        accessor: (row: TrafficBySourceTableRow) => row.label,
        id: 'label',
      },
      {
        Header: 'Opened Widget',
        accessor: (row: TrafficBySourceTableRow) => row.openedWidget,
        id: 'openedWidget',
        Cell: ({ value }: { value: number }) => formatCount(value),
      },
      {
        Header: 'Online Appts',
        accessor: (row: TrafficBySourceTableRow) => row.onlineAppts,
        id: 'onlineAppts',
        Cell: ({ value }: { value: number }) => formatCount(value),
      },
      {
        Header: 'Total Appts',
        accessor: (row: TrafficBySourceTableRow) => row.totalAppts,
        id: 'totalAppts',
        Cell: ({ value }: { value: number }) => formatCount(value),
      },
      {
        Header: 'Shows',
        id: 'shows',
        disableSortBy: true,
        Cell: () => SHOWS_PLACEHOLDER,
      },
      {
        Header: 'New Patients',
        accessor: (row: TrafficBySourceTableRow) => row.totalNewPatients,
        id: 'totalNewPatients',
        Cell: ({ value }: { value: number }) => formatCount(value),
      },
      {
        Header: 'Existing Patients',
        accessor: (row: TrafficBySourceTableRow) => row.totalExistingPatients,
        id: 'totalExistingPatients',
        Cell: ({ value }: { value: number }) => formatCount(value),
      },
      {
        Header: 'Production',
        accessor: (row: TrafficBySourceTableRow) => row.totalProduction,
        id: 'totalProduction',
        Cell: ({ value }: { value: number }) => (
          <span className="font-bold">{formatUsdCurrency(value)}</span>
        ),
      },
      {
        Header: 'Production / Appt',
        accessor: (row: TrafficBySourceTableRow) => row.productionPerAppt,
        id: 'productionPerAppt',
        Cell: ({ value }: { value: number }) => formatUsdCurrency(value),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: rows,
      disableSortRemove: true,
    },
    useSortBy
  );

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white">
      <span className="text-16 font-bold leading-[2.4rem] block mb-1.6">
        Traffic by Source
      </span>

      <div className="flex items-center gap-x-0.6 mb-1.6">
        <InfoIcon className="shrink-0" />
        <span className="text-12 text-dim-grey leading-[1.8rem]">
          Traffic by Source data is only available from{' '}
          {moment(TRAFFIC_BY_SOURCE_START_DATE).format('MMM DD, YYYY')} onward.
        </span>
      </div>

      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="w-full text-14 leading-[2.1rem] table-fixed"
        >
          <thead className="font-bold">
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className="border-b border-light-grey"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-left p-0 py-1.9 px-1.6"
                  >
                    <div
                      className={`flex items-center gap-x-0.6 ${
                        column.canSort ? 'cursor-pointer' : ''
                      }`}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem] w-min">
                        {column.render('Header')}
                      </span>
                      {column.canSort && (
                        <SortArrow
                          className={`shrink-0 ${
                            column.isSorted ? 'fill-magenta' : 'fill-[#9CA3AF]'
                          } ${
                            column.isSorted && !column.isSortedDesc
                              ? 'rotate-180'
                              : ''
                          } transition-all`}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {(isLoading || isValidating) ? (
            <tbody>
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <div className="w-full h-[24rem] flex justify-center items-center">
                    <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()} className="text-darkest-grey">
              {tableRows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="bg-white border-b border-light-grey group"
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="text-left p-0 py-1.9 px-1.6"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}

          {!isLoading && totals && (
            <tfoot>
              <tr className="bg-pink-01 font-bold text-darkest-grey">
                <td className="text-left p-0 py-1.9 px-1.6">Total</td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatCount(totals.openedWidget)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatCount(totals.onlineAppts)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatCount(totals.totalAppts)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {SHOWS_PLACEHOLDER}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatCount(totals.totalNewPatients)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatCount(totals.totalExistingPatients)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6 text-magenta">
                  {formatUsdCurrency(totals.totalProduction)}
                </td>
                <td className="text-left p-0 py-1.9 px-1.6">
                  {formatUsdCurrency(totals.productionPerAppt)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default TrafficBySource;
